import { GtmhubController } from "@gtmhub/core";
import { getGoogleCharts } from "@gtmhub/insights";
import { addItemsIfChartValueIsArray } from "@gtmhub/lists/chart-utils";
import { ChartSettingsModalOpener } from "@gtmhub/lists/services/chart-settings-modal-opener";
import { IComponentOptions, IController, IPromise, IQService, IScope } from "angular";
import {
  aggregationSettingToGoogleAggregationMap as AggregationMap,
  chartViewToChartTypeMap,
  DEFAULT_LIST_CHART_AGGREGATION,
  DEFAULT_LIST_CHART_COLOR,
  IListColumn,
  IListFilters,
  IListProperty,
  ILoadList,
  ListItem,
  ListOptions,
  ListService,
  ListTypeChangeEventArgs,
  ListTypes,
  ListView,
} from "../..";
import { ChartSettingsLoader } from "../../services/chart-settings-loader";
export interface IListChartBindings {
  view: ListView;
  filters: IListFilters;
  listId: string;
  listType: ListTypes;
  listProperties: IListProperty[];
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ListChartsComponentCtrl extends IListChartBindings {}

export class ListChartsComponentCtrl extends GtmhubController implements IController {
  public static $inject = ["$scope", "ListService", "ChartSettingsLoader", "ChartSettingsModalOpener", "$q"];
  private google: typeof google;

  private dataToDrawFrom: google.visualization.DataTable;
  private partialListData: ListItem[];
  private allListData: ListItem[];
  private loadAllListDataPromise: IPromise<void>;

  constructor(
    private $scope: IScope,
    private listService: ListService,
    private chartSettingsLoader: ChartSettingsLoader,
    private chartSettingsModalOpener: ChartSettingsModalOpener,
    private $q: IQService
  ) {
    super();
  }

  $onInit(): void {
    this.chartSettingsLoader
      .promise()
      .then(() => this.googleCharts())
      .then((google) => {
        this.google = google;
        this.initialLoadAndDraw();

        this.$scope.$on("chartSettingsChange", (event, changedFrom: string) => this.onChartSettingsChange(changedFrom));
        this.$scope.$on("listFiltersChange", (event, filters: IListFilters) => this.onFiltersChange(filters));
        this.$scope.$on("listViewChange", (event, view: ListView) => this.onListViewChange(view));
        this.$scope.$on("listTypeChange", (event, args: ListTypeChangeEventArgs) => this.onListTypeChange(args));
      });
  }

  // TODO: move into getScripts service and use everywhere we use googleCharts
  googleCharts() {
    return this.$q.when(getGoogleCharts()).then((google) => google);
  }

  onChartSettingsClicked(): void {
    this.chartSettingsModalOpener.open(this.listProperties);
  }

  private onChartSettingsChange(changedFrom: string): void {
    if (changedFrom === ListChartsComponentCtrl.name) {
      this.initialLoadAndDraw();
    } else {
      this.loadAllListDataPromise.then(() => {
        this.constructChartData(this.allListData);
        this.drawChart();
      });
    }
  }

  private initialLoadAndDraw(): void {
    this.loadPartialListData().then(() => {
      this.constructChartData(this.partialListData);
      this.drawChart();
    });

    this.loadAllListDataPromise = this.loadAllListData();
  }
  private loadPartialListData(): IPromise<void> {
    const { chartCategory, chartValue } = this.chartSettingsLoader.chartSettings;
    const columns = [
      { fieldName: chartCategory.fieldName, type: chartCategory.dataType },
      { fieldName: chartValue.fieldName, type: chartValue.dataType },
    ];

    const options: ILoadList = this.constructListLoadOptions(columns);

    return this.listService.loadList({ id: this.listId, body: options }).then((collection) => {
      this.partialListData = collection.items;
    });
  }

  private loadAllListData(): IPromise<void> {
    const columns = this.listProperties.map((c) => {
      return { fieldName: c.fieldName, type: c.dataType };
    });

    const options: ILoadList = this.constructListLoadOptions(columns);

    return this.listService.loadList({ id: this.listId, body: options }).then((collection) => {
      this.allListData = collection.items;
    });
  }

  private constructListLoadOptions(columns: IListColumn[]): ILoadList {
    return {
      [ListOptions.columns]: columns,
      [ListOptions.filter]: this.filters,
      [ListOptions.sort]: [],
      [ListOptions.type]: this.listType,
      limit: -1,
    };
  }

  private constructChartData(items: ListItem[]): void {
    const { chartSettings: chartSettings } = this.chartSettingsLoader;

    const formattedListData = items.flatMap((item) =>
      addItemsIfChartValueIsArray(item, {
        chartValueFieldName: chartSettings.chartValue.fieldName,
        chartCategoryFieldName: chartSettings.chartCategory.fieldName,
      })
    );

    const arrayData = [[chartSettings.chartCategory.displayName, chartSettings.chartValue.displayName], ...formattedListData];
    const dataTable = this.google.visualization.arrayToDataTable(arrayData);

    if (!chartSettings.aggregation) {
      this.dataToDrawFrom = dataTable;
    } else {
      const googleAggregation = AggregationMap.get(chartSettings.aggregation);
      const aggregatedData = this.google.visualization.data.group(
        dataTable,
        [0],
        [{ column: 1, aggregation: this.google.visualization.data[googleAggregation], type: "number" }]
      );
      this.dataToDrawFrom = aggregatedData;
    }
  }

  private drawChart(): void {
    if (this.google && this.view !== ListView.list) {
      const chartType = chartViewToChartTypeMap.get(this.view);
      const chart = new this.google.visualization[chartType](document.getElementById("chart"));
      chart.draw(this.dataToDrawFrom, { width: 1300, height: 750 });
    }
  }

  private onListViewChange(view: ListView): void {
    this.view = view;
    this.drawChart();
  }

  private onFiltersChange(filters: IListFilters): void {
    this.filters = filters;
    this.initialLoadAndDraw();
  }

  private onListTypeChange(args: ListTypeChangeEventArgs): void {
    this.listType = args.listType;
    this.listProperties = args.listProperties;
    this.filters = args.filters;

    const chartSettings = {
      color: DEFAULT_LIST_CHART_COLOR,
      aggregation: DEFAULT_LIST_CHART_AGGREGATION,
      chartCategory: this.listProperties[0],
      chartValue: this.listProperties[1],
    };

    this.chartSettingsLoader.chartSettingsChange(chartSettings, ListChartsComponentCtrl.name);
  }
}

export const ListChartsComponent: IComponentOptions = {
  templateUrl: require("./list-chart.component.html"),
  controller: ListChartsComponentCtrl,
  bindings: {
    view: "<",
    filters: "<",
    listId: "<",
    listType: "<",
    listProperties: "<",
  },
};
